import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useData } from '@hooks/useData';
import { useNavigate } from 'react-router-dom';
import { Role } from '@types';

const ENDPOINT_URL = '/draft-registration/step-1';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type RegistrationFirstStepInput = paths[url]['post']['requestBody']['content']['multipart/form-data'];
export type RegistrationFirstStepSuccessResponse = paths[url]['post']['responses']['200']['content']['application/json'];

export const useRegistrationFirstStep = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const navigate = useNavigate();
    const { setDraft, setFirstStep, setCarInfoStep, setOtpStep, roles, selectedRole, setSelectedRole } =
        useData();
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [validationErrors, setValidationErrors] = useState<any | null>(null);

    const request = async (props: RegistrationFirstStepInput, opts?: { onNext?: (skipOtp?: boolean) => void }) => {
        setLoading(true);
        setSuccess(null);
        setError(null);
        setErrorMessage(null);
        setValidationErrors(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...props }),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;

                    if (json.data === 'EMAIL_ALREADY_REGISTERED') {
                        navigate('/login');
                    }
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    error.message = '';
                }

                error.statusCode = response.status;
                throw error;
            }

            const rawResponse = await response.json();
            const data = rawResponse.data;
            const selectedRoleId = selectedRole?.id;
            const role = roles?.data.find((r: Role) => r.id === selectedRoleId) ?? null;

            setSelectedRole(role);

            setDraft({
                uuid: data?.uuid ?? null,
                email: data?.email ?? null,
                current_step: data?.current_step ?? null,
            });

            setOtpStep?.({
                verified: data.otp?.validated ?? false,
                timestamp: Date.now(),
            });
            setFirstStep({ data });

            setCarInfoStep({
                data: {
                    uuid: data?.uuid,
                    brand: data?.step_2?.brand || '',
                    model: data?.step_2?.model || '',
                    color: data?.step_2?.color || '',
                    plate: data?.step_2?.plate || '',
                    seats: data?.step_2?.seats ?? 0,
                },
                success: data.success ?? true,
                message: data.message ?? '',
            });

            const otp = data.otp;
            const currentStep = data?.current_step;

            if (otp && currentStep) {
                const skipOtp = Boolean(otp?.validated ?? false);
                const hasRedirectedReturnedUser = localStorage.getItem('previousPage') ? true : false;

                if (currentStep && currentStep > 1 && hasRedirectedReturnedUser && role?.name === 'Driver') {
                    localStorage.removeItem('previousPage');
                    switch (currentStep) {
                        case 2:
                        case 4:
                            navigate('/register-driver-account/password');
                            break;
                        default:
                            console.error('Unknown step', currentStep);
                    }
                } else {
                    localStorage.removeItem('previousPage');
                    opts?.onNext?.(skipOtp);
                }
            }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            error.message && setError(error.message);
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            error.errors && setValidationErrors(error.errors);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };
    return { request, loading, success, errorMessage, error, validationErrors, lastResponseTime };
};
